import { R2_PUBLIC_URL } from '@core/config/constants';
import {
  getConfiguredCatalogShopExclusions,
  getConfiguredCatalogShopIds,
  getConfiguredPrimaryShopId,
  isStorefrontCatalogShopExcluded,
  MULTI_SHOP_CONFIG_ENABLED,
} from '@core/config/storefront';
import type { TenantShop } from '@core/tenant/types';

/** Build-time product mode from `src/core/config/storefront.ts`, not env. */
export const MULTI_SHOP_ENABLED = MULTI_SHOP_CONFIG_ENABLED;

/** Default / single-store shop UUID from source-controlled storefront config. */
export function getEnvShopId(): string {
  return getConfiguredPrimaryShopId();
}

export function getFirstShopId(shops: Pick<TenantShop, 'id'>[]): string {
  return shops[0]?.id ?? getEnvShopId();
}

/**
 * Shop whose homepage hero is suppressed in marketplace (`multi-shop`) mode.
 * Matches `STOREFRONT_CONFIG.primaryShopId` — the umbrella storefront, not seller promos.
 */
export function getHomeBannerExcludedShopId(): string {
  return getConfiguredPrimaryShopId();
}

export function isHomeBannerShopEligible(shopId: string, multiShop: boolean): boolean {
  if (!multiShop) return true;
  return shopId !== getHomeBannerExcludedShopId();
}

/** Shops to fan-out banner queries to (marketplace excludes the primary shop). */
export function getShopsForHomeBannerFetch(
  shops: Pick<TenantShop, 'id'>[],
  multiShop: boolean
): Pick<TenantShop, 'id'>[] {
  if (!multiShop) return shops;
  return shops.filter((shop) => isHomeBannerShopEligible(shop.id, true));
}

/** Active shop for single-store banner scope (tenant list, then configured primary). */
export function getHomeBannerShopId(shops: Pick<TenantShop, 'id'>[]): string {
  return getFirstShopId(shops);
}

export function filterHomeBannerSliders<T extends { shop_id?: string | null }>(
  sliders: T[],
  multiShop: boolean
): T[] {
  return sliders.filter((item) => {
    const sid = item.shop_id ?? '';
    if (sid && isStorefrontCatalogShopExcluded(sid)) return false;
    if (!multiShop) return true;
    return sid !== getHomeBannerExcludedShopId();
  });
}

/**
 * Source-controlled UUIDs merged with `mst_shop` for catalog fan-out.
 * Kept as `getExtraCatalogShopIds` for compatibility with existing call sites.
 */
export function getExtraCatalogShopIds(): string[] {
  return getConfiguredCatalogShopIds();
}

/** Drops admin-only shops from tenant fan-out (catalog, categories, banners, recs). */
export function applyStorefrontCatalogShopVisibility<T extends Pick<TenantShop, 'id'>>(
  shops: T[]
): T[] {
  const excluded = getConfiguredCatalogShopExclusions();
  if (!excluded.length) return shops;
  return shops.filter((shop) => !isStorefrontCatalogShopExcluded(shop.id));
}

export { isStorefrontCatalogShopExcluded } from '@core/config/storefront';

/**
 * Image base for relative storage paths.
 * Prefer `tenantShop.imageCdnUrl`, then global R2 URL.
 */
export function getImageBaseForTenantShop(
  shopId: string | undefined,
  tenantShops: TenantShop[]
): string {
  if (shopId) {
    const row = tenantShops.find((s) => s.id === shopId);
    const fromRow = row?.imageCdnUrl?.trim();
    if (fromRow) return fromRow.replace(/\/+$/, '');
  }
  return R2_PUBLIC_URL.replace(/\/+$/, '');
}
